import React, { useState, useEffect, useMemo } from 'react';
import qs from 'qs';

import { loadImpersonateData } from '../Impersonate/state';
import BasicAuthLogin from '../BasicAuthLogin';

export const DEFAULT_LOGIN_HOST = 'https://accounts.density.io';

function parseHashParams() {
  return qs.parse(window.location.hash.replace(/^#\/?/, ''));
}

function removeTokenFromHash() {
  const hashParams = parseHashParams();
  if (!hashParams.access_token) {
    return;
  }

  const url = new URL(window.location.href);
  url.hash = '';
  window.history.replaceState({}, '', url.toString());
}

// Called when the browser should be redirected to visit the login page.
function redirectToLogin(loginHost: string, tokenLocalStorageKey: string) {
  removeTokenFromHash();
  localStorage.removeItem(tokenLocalStorageKey);

  const url = new URL(window.location.href);
  url.hash = `#/access_token=%TOKEN%`;
  const redirectUrl = url.toString();
  window.location.href = `${loginHost}/v2/app/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
}

// TODO: add a type for this to @density/lib-api-types
export type TokenCheckResponse = {
  exp: number;
  iat: number;
  user?: {
    id: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  permissions: Array<string>;
};

export function verifyUserForInternalDensityTool(tokenCheckResponse: TokenCheckResponse) {
  return tokenCheckResponse.permissions.includes('impersonate');
}

export type TokenProviderData = {
  token: string;
  tokenCheckResponse: TokenCheckResponse | null;
  xImpersonateUserHeader?: string;
  headers: { [key: string]: string };
};
type TokenProviderContextData = TokenProviderData & {
  setXImpersonateUserHeader: (header?: string) => void;
  loginHost: string;
};
export const TokenProviderContext = React.createContext<TokenProviderContextData | null>(null);

type TokenProviderProps = {
  /**
   * Optional - but highly recommended.
   * A fallback view to render while user data is loading
   */
  fallback?: JSX.Element | null;
  /**
   * View to show when there is an error.
   * May be JSX, or a function that takes an error message as its argument and returns JSX.
   */
  error?: JSX.Element | null | ((message: string) => JSX.Element | null);
  verifyUser?: (tokenCheckResponse: TokenCheckResponse) => boolean;
  tokenLocalStorageKey?: string;
  overrideTokenParam?: string;
  loginHost?: string;

  // If set, configures the TokenProvider to only allow logins via basic auth, bypassing auth0 all
  // together.
  basicAuth?: {
    // The name of the app to show in the header
    headerAppName: React.ReactNode;
    // The name of the app to show in the "Log in" button. If null, no name is shown.
    buttonAppName: React.ReactNode;
  };

  // NOTE: explicitly defining children here
  children: (tokenProviderData: TokenProviderData) => React.ReactNode;
};

/**
 * NOTE: not using React.FunctionComponent (which specifies `children` loosely)
 * because in this case we know that `children` must be a function
 */
const TokenProvider: React.FunctionComponent<TokenProviderProps> = (props: TokenProviderProps) => {
  const {
    fallback = null,
    error = null,
    children,
    verifyUser = () => true,
    tokenLocalStorageKey = 'token',
    overrideTokenParam = null,
    loginHost = DEFAULT_LOGIN_HOST,
    basicAuth = null,
  } = props;
  const impersonateData = loadImpersonateData();
  const [xImpersonateUserHeader, setXImpersonateUserHeader] = useState<string | undefined>(
    impersonateData.enabled ? impersonateData.id : undefined
  );

  const [token, setToken] = useState(window.localStorage.getItem(tokenLocalStorageKey));

  const [status, setStatus] = useState('LOADING');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // When a token is not in localstorage, handle the first half of the auth process:
  // 1. Redirect users to auth0 login page
  // 2. When redirected back to the app, exchange the auth0 temporary token for a session token. Set this in localstorage.
  useEffect(() => {
    // Check the query string for an override token
    const qs = new URL(window.location.href).searchParams;
    const overrideToken = overrideTokenParam && qs.get(overrideTokenParam);
    if (overrideToken) {
      setToken(overrideToken);
      return;
    }

    // See if there's a token that has just been provided
    const hashParams = parseHashParams();
    if (hashParams.access_token) {
      const run = async () => {
        const resp = await window.fetch(`${loginHost}/v2/tokens/exchange/auth0/`, {
          method: 'POST',
          headers: {
            Authorization: `JWT ${hashParams.access_token}`,
          },
        });

        if (!resp.ok) {
          setStatus('ERROR');
          const body = await resp.json();
          setErrorMessage(body.detail || 'Error logging in!');
          return;
        }

        const exchangedToken = await resp.json();
        window.localStorage.setItem(tokenLocalStorageKey, exchangedToken);
        removeTokenFromHash();
        setToken(exchangedToken);
      };
      run().catch((e) => {
        setStatus('ERROR');
        setErrorMessage('Error logging in!');
      });
      return;
    }

    // If the auth0 token is already in the url, then there's no need to redirect to the login page.
    if (token !== null) {
      return;
    }
    // If basic auth is enabled, then don't redirect to the login page.
    if (basicAuth) {
      return;
    }
    redirectToLogin(loginHost, tokenLocalStorageKey);
  }, [token, loginHost, tokenLocalStorageKey, basicAuth, overrideTokenParam]);

  // On each page load where a user is logged in:
  // 1. Get user details from /v2/users/me, and store this information
  // 2. Ensure that they can access the application
  const [tokenCheckResponse, setTokenCheckResponse] = useState(null);

  useEffect(() => {
    if (token === null) {
      return;
    }
    if (tokenCheckResponse !== null) {
      return;
    }

    async function run() {
      // Get the token details, and ensure that this token has the correct
      // authorization to use this app.
      const response = await fetch(`${loginHost}/v2/tokens/check`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // If the token is invalid, redirect back to the login page.
      if (response.status === 403) {
        redirectToLogin(loginHost, tokenLocalStorageKey);
        return;
      }
      if (!response.ok) {
        setStatus('ERROR');
        setErrorMessage('Error getting user permissions!');
        return;
      }
      const tokenCheckBody = await response.json();

      const userIsVerified = await verifyUser(tokenCheckBody);
      if (!userIsVerified) {
        setStatus('ERROR');
        setErrorMessage('This user is not authorized to access this system.');
        return;
      }

      setTokenCheckResponse(tokenCheckBody);
      setStatus('COMPLETE');
    }
    run().catch(() => {
      setStatus('ERROR');
      setErrorMessage('Error logging in!');
    });
  }, [token, verifyUser, loginHost, tokenLocalStorageKey, tokenCheckResponse]);

  const memoedContextData = useMemo(() => {
    if (!token) {
      return;
    }

    const headers: { [key: string]: string } = {
      Authorization: `Bearer ${token}`,
    };
    if (xImpersonateUserHeader) {
      headers['X-Impersonate-User'] = xImpersonateUserHeader;
    }

    const data: TokenProviderContextData = {
      token,
      tokenCheckResponse,
      xImpersonateUserHeader,
      headers,
      setXImpersonateUserHeader,
      loginHost,
    };

    return data;
  }, [loginHost, token, tokenCheckResponse, xImpersonateUserHeader]);

  switch (status) {
    case 'LOADING':
      if (basicAuth) {
        return (
          <BasicAuthLogin
            headerAppName={basicAuth.headerAppName}
            buttonAppName={basicAuth.buttonAppName}
            loginHost={loginHost}
            onLoginComplete={setToken}
          />
        );
      }
      return fallback;
    case 'ERROR':
      if (typeof error === 'function') {
        return error(errorMessage || '');
      } else {
        return error;
      }
    case 'COMPLETE':
      if (!memoedContextData) {
        return fallback;
      }

      const Provider = TokenProviderContext.Provider;

      return <Provider value={memoedContextData}>{children(memoedContextData)}</Provider>;
    default:
      return null;
  }
};

TokenProvider.displayName = 'TokenProvider';
export default TokenProvider;
